import { useEffect, useMemo, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

const REMARK_PLUGINS = [remarkGfm];
const KNOWLEDGE_ARTICLE_REMARK_PLUGINS = [remarkGfm, remarkKnowledgeWikiLinks];
const PREVIEW_CLASS = "min-h-full bg-editor-bg";
import { documentMarkdownComponents } from "@/components/common/markdown-components";
import { WorkspaceMarkdownLink } from "@/components/common/WorkspaceMarkdownLink";
import { openKnowledgeArticleByName } from "@/lib/knowledge-article-open";
import { knowledgeArticleNameFromHref, remarkKnowledgeWikiLinks } from "@/lib/knowledge-wikilinks";
import { splitPath } from "@/lib/environment-files";
import { BODY_LINK_CLASS } from "@/lib/link-styles";
import { fetchFile } from "@/lib/download-file";
import { PreviewSkeleton } from "@/components/preview/PreviewSkeleton";

interface MarkdownPreviewProps {
	url: string;
	/** ワークスペース相対のファイルパス。文書内の相対リンクはこのファイルのディレクトリを基準に開く。 */
	path: string;
	/**
	 * 指定すると fetch をスキップし、この文字列を表示する。Source タブでの未保存編集内容を
	 * Preview タブに反映するために使う (呼び出し側が dirty のときだけ渡す想定)。
	 */
	source?: string;
	/** ナレッジグラフの記事なら、本文の [[記事名]] を記事を開くリンクにする。 */
	knowledgeArticle?: boolean;
}

export function MarkdownPreview({
	url,
	path,
	source,
	knowledgeArticle = false,
}: MarkdownPreviewProps) {
	const [content, setContent] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (source !== undefined) return;
		let cancelled = false;
		setContent(null);
		setError(null);
		fetchFile(url)
			.then(async (r) => {
				if (!r.ok) throw new Error(`プレビューの取得に失敗しました (${r.status})`);
				const text = await r.text();
				if (!cancelled) setContent(text);
			})
			.catch((err: unknown) => {
				console.error("MarkdownPreview fetch failed", err);
				if (!cancelled) setError("プレビューを読み込めませんでした。");
			});
		return () => {
			cancelled = true;
		};
	}, [url, source]);

	const effective = source ?? content;
	const baseDir = splitPath(path).dir;
	const components = useMemo<Components>(
		() => ({
			...documentMarkdownComponents,
			a: ({ href, children }) => {
				const articleName = knowledgeArticleNameFromHref(href);
				// href を付けるとアプリの URL として解決されるため、ワークスペースのファイルのリンクと同じく付けない。
				return articleName === null ? (
					<WorkspaceMarkdownLink href={href} baseDir={baseDir} asPreview={false}>
						{children}
					</WorkspaceMarkdownLink>
				) : (
					<a
						className={BODY_LINK_CLASS}
						onClick={() => void openKnowledgeArticleByName(articleName)}
					>
						{children}
					</a>
				);
			},
		}),
		[baseDir],
	);

	// マークダウンのパースは内容に依存するため、リサイズ等による再レンダーでは再実行しない。
	const rendered = useMemo(
		() =>
			effective === null ? null : (
				<ReactMarkdown
					remarkPlugins={knowledgeArticle ? KNOWLEDGE_ARTICLE_REMARK_PLUGINS : REMARK_PLUGINS}
					components={components}
				>
					{effective}
				</ReactMarkdown>
			),
		[effective, components, knowledgeArticle],
	);

	if (error)
		return (
			<div className={PREVIEW_CLASS}>
				<p className="m-0 p-4 text-caption text-danger">{error}</p>
			</div>
		);
	if (effective === null)
		return (
			<div className={PREVIEW_CLASS}>
				<PreviewSkeleton />
			</div>
		);

	return (
		<div className={`${PREVIEW_CLASS} select-text p-4 text-ui leading-relaxed text-fg-1`}>
			{rendered}
		</div>
	);
}
